import { useState, useEffect, useRef } from "react";
import api from "../utils/axios";
import { useForm, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import * as XLSX from "xlsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Upload, X } from 'lucide-react';
import { hasRole } from "../utils/roles";
import { blockNegativeKey, blockWheelChange } from "../utils/numberInput";

const statuses = ["Live", "Completed", "Paused", "Declined", "Proof Submitted"];
const pacingOptions = ["Weekly", "Monthly", "Custom", "Front Load", "NA"];
const priorityOptions = ["NA", "P1", "P2", "P3"];
const platformOptions = [
  "Promowise", "Integrate", "Direct", "Demand Works", "Intentsify", "Converter",
];
const deliveryDayOptions = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Field = ({ label, required, children, className = "" }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm font-medium text-muted-foreground">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

// ─── Upload Result Modal ────────────────────────────────────────────────────
function UploadResultModal({ result, onClose, originalFile }) {
  if (!result) return null;
  const { successCount, failCount, rows } = result;

  const downloadReport = () => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const enriched = jsonRows.map((row, i) => {
        const match = rows[i];
        return {
          ...row,
          "Upload Status": match?.status || "Unknown",
          "Failure Reason": match?.reason || "",
        };
      });

      const newWs = XLSX.utils.json_to_sheet(enriched);
      newWs["!cols"] = Object.keys(enriched[0] || {}).map(() => ({ wch: 20 }));
      const newWb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(newWb, newWs, "Upload Report");
      XLSX.writeFile(newWb, "upload_report.xlsx");
    };
    reader.readAsArrayBuffer(originalFile);
  };

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-card text-card-foreground rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden border border-border animate-fadeIn">

      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Upload Report</h2>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-sm"
        >
          <X size={15} />
        </button>
      </div>

      {/* Summary counts */}
      <div className="px-6 py-4 flex gap-3">
        {/* Success */}
        <div className="flex-1 rounded-lg border border-border bg-muted/40 p-4 text-center">
          <p className="text-3xl font-bold text-primary dark:text-[#c6f000]">{successCount}</p>
          <p className="text-xs text-muted-foreground mt-1 font-medium">Campaigns Added</p>
        </div>
        {/* Failed */}
        <div className="flex-1 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
          <p className="text-3xl font-bold text-destructive">{failCount}</p>
          <p className="text-xs text-muted-foreground mt-1 font-medium">Failed</p>
        </div>
        {/* Total */}
        <div className="flex-1 rounded-lg border border-border bg-muted/40 p-4 text-center">
          <p className="text-3xl font-bold text-foreground">{successCount + failCount}</p>
          <p className="text-xs text-muted-foreground mt-1 font-medium">Total Rows</p>
        </div>
      </div>

      {/* Row-level log */}
      {rows.length > 0 && (
        <div className="px-6 pb-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Row Details
          </p>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border text-sm">
            {rows.map((r, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 px-4 py-2.5 transition-colors
                  ${r.status === "failed"
                    ? "bg-destructive/5 hover:bg-destructive/10"
                    : "bg-muted/20 hover:bg-muted/40"
                  }`}
              >
                {/* Icon badge */}
                <span
                  className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                    ${r.status === "failed"
                      ? "bg-destructive/20 text-destructive"
                      : "bg-[#c6f000]/20 text-[#c6f000] dark:bg-[#c6f000]/20 dark:text-[#c6f000]"
                    }`}
                >
                  {r.status === "failed" ? "✕" : "✓"}
                </span>

                <div className="min-w-0">
                  <span className="font-semibold text-foreground">Row {r.row}: </span>
                  <span className="text-muted-foreground">{r.campaignName || "(unnamed)"}</span>
                  {r.reason && (
                    <p className="text-destructive text-xs mt-0.5">{r.reason}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-4 border-t border-border flex justify-between items-center">
        {originalFile && (
          <Button variant="outline" onClick={downloadReport} className="text-sm gap-1.5">
            ⬇ Download Report
          </Button>
        )}
        <Button onClick={onClose} className="ml-auto text-sm">
          Close
        </Button>
      </div>

    </div>
  </div>
);
}
// ─── Bulk Upload Section ────────────────────────────────────────────────────
function BulkUpload({ currentUser }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);

  // Template now comes from the backend so dropdowns always reflect the
  // current option lists below and live CM/CSM/Sales Manager names.
  const downloadTemplate = async () => {
    try {
      const response = await api.post(
        "/campaigns/template",
        { statuses, platformOptions, deliveryDayOptions, pacingOptions, priorityOptions },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "campaign_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to generate template");
    }
  };

  const processFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please upload a valid .xlsx or .xls file");
      return;
    }

    setOriginalFile(file);
    setUploading(true);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      if (jsonRows.length === 0) {
        toast.error("Excel file is empty");
        setUploading(false);
        return;
      }

      const res = await api.post("/campaigns/bulk", {
        rows: jsonRows,
        senderName: currentUser?.name,
        senderEmail: currentUser?.email,
      });

      setUploadResult(res.data);
    } catch (e) {
      toast.error(
        "Upload failed: " + (e?.response?.data?.message || e.message)
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  return (
    <>
<Card className="mb-6">
  <CardHeader>
    <div className="flex items-center justify-between">
      <CardTitle>Bulk Upload Campaigns</CardTitle>
      <Button variant="outline" size="sm" onClick={downloadTemplate}>
        ⬇ Download Template
      </Button>
    </div>
  </CardHeader>
  <CardContent>
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`group border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
        ${dragOver
          ? "border-primary bg-primary/5"
          : "border-border hover:border-lime-600 hover:bg-lime-100 dark:hover:border-[#c6f000] dark:hover:bg-[#c6f000]/5"
        }`}
    >
      <div className="flex justify-center mb-3">
        <Upload className="w-8 h-8 transition-colors text-muted-foreground group-hover:text-lime-600 dark:group-hover:text-[#c6f000]" />
      </div>
      <p className="text-sm font-medium text-foreground transition-colors group-hover:text-lime-600 dark:group-hover:text-[#c6f000]">
        {uploading
          ? "Processing..."
          : "Drop your Excel file here or click to browse"}
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        .xlsx or .xls — use the template above for correct column headers
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  </CardContent>
</Card>

      <UploadResultModal
        result={uploadResult}
        originalFile={originalFile}
        onClose={() => setUploadResult(null)}
      />
    </>
  );
}

// ─── Main Admin Page ────────────────────────────────────────────────────────
export default function Admin() {
  const {
    register, handleSubmit, setValue, watch, reset, control,
    formState: { errors },
  } = useForm();

  const endDateTouched = useRef(false);
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });
  const selectedPacing = useWatch({ control, name: "pacingType" });
  const allocation = useWatch({ control, name: "allocation" });
  const accepted = useWatch({ control, name: "accepted" });

  const [saving, setSaving] = useState(false);
  const [CMs, setCMs] = useState([]);
  const [managers, setManagers] = useState([]);
  const [csmList, setCsmList] = useState([]);
  const [s, sets] = useState([]);
  // Whether the logged-in user holds the CSM designation (they may hold others too,
  // e.g. "Admin,CSM" — hasRole() checks membership, not exact equality).
  const [isCsmUser, setIsCsmUser] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const a = Number(allocation) || 0;
    const acc = Number(accepted) || 0;
    setValue("outstanding", a - acc >= 0 ? a - acc : 0);
  }, [allocation, accepted, setValue]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      const csm = hasRole(user, "csm");
      setIsCsmUser(csm);
      if (csm) setValue("CSM", user.name);
    }
    api.get("/users/by-role?designation=CM").then((r) => setCMs(r.data)).catch(() => toast.error("Failed loading CMs"));
    api.get("/users/by-role?designation=Manager").then((r) => setManagers(r.data)).catch(() => toast.error("Failed loading managers"));
    api.get("/users/by-role?designation=CSM").then((r) => setCsmList(r.data)).catch(() => toast.error("Failed loading CSMs"));
    
  }, []);

  useEffect(() => {
    if (!endDateTouched.current) return;
    if (startDate && endDate && endDate < startDate) {
      toast.error("End date cannot be before Start date");
      setValue("endDate", null);
      endDateTouched.current = false;
    }
  }, [startDate, endDate, setValue]);

  const onSubmit = async (form) => {
    setSaving(true);
    try {
      const payload = {
        status: form.status || null,
        platform: form.platform || null,
        internalCID: form.internalCID || null,
        clientSID: form.clientSID || null,
        campaignName: form.campaignName || null,
        liveDate: toUTCISOString(form.liveDate),
        startDate: toUTCISOString(form.startDate),
        endDate: toUTCISOString(form.endDate),
        clientName: form.clientName || null,
        deliveryDay: form.deliveryDay || null,
        pacing: (() => {
          const type = form.pacingType;
          const value = form.pacingValue;
          if (!type || type === "NA") return "NA";
          return value ? `${type} - ${value}` : type;
        })(),
        allocation: form.allocation ? Number(form.allocation) : 0,
        accepted: form.accepted ? Number(form.accepted) : 0,
        rejected: form.rejected ? Number(form.rejected) : 0,
        pendingQA: form.pendingQA ? Number(form.pendingQA) : 0,
        outstanding: form.outstanding !== undefined ? Number(form.outstanding) : 0,
        priority: form.priority || null,
        CM: form.CM || null,
        CSM: form.CSM || null,
        salesManagers: form.salesManagers || [],
        io_po_number: form.io_po_number || null,
        so_number: form.so_number || null,
      };

      await api.post("/campaigns", {
        ...payload,
        senderName: currentUser?.name,
        senderEmail: currentUser?.email,
      });

      toast.success("New Campaign Added");
      reset(emptyForm);
      endDateTouched.current = false;
    } catch (e) {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  function toUTCISOString(date) {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)).toISOString();
  }

  const logout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const emptyForm = {
    status: null, platform: "", internalCID: "", clientSID: "", campaignName: "",
    liveDate: null, startDate: null, endDate: null, clientName: null,
    deliveryDay: "", pacingType: "", pacingValue: "", allocation: "", accepted: "",
    rejected: "", pendingQA: "", outstanding: "", priority: "", CM: null,
    CSM: isCsmUser ? currentUser?.name : null,
    salesManagers: [], io_po_number: "", so_number: "",
  };

  return (
    <div className="mx-auto px-6 py-8">
      <Navbar logout={logout} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl">Add New Campaign</h1>
        <Button asChild variant="outline">
          <Link to="/">Back to Dashboard</Link>
        </Button>
      </div>

      {/* ── Bulk Upload ── */}
      <BulkUpload currentUser={currentUser} />

      {/* ── Manual Form ── */}
      <Card>
        <CardHeader>
          <CardTitle>Add Campaign Manually</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit, () => toast.error("Please fill all required fields"))}
            className="grid grid-cols-2 gap-4"
          >
            {/* STATUS */}
            <Field label="Status" required>
              <Select onValueChange={(v) => setValue("status", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("status", { required: "Status is required" })} />
            </Field>

            {/* PLATFORM */}
            <Field label="Platform" required>
              <Select onValueChange={(v) => setValue("platform", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select Platform" /></SelectTrigger>
                <SelectContent>{platformOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("platform", { required: "Platform is required" })} />
            </Field>

            {/* INTERNAL CID — optional */}
            <Field label="Internal CID">
              <Input placeholder="" {...register("internalCID")} />
            </Field>

            {/* CLIENT SID */}
            <Field label="Client SID" required>
              <Input placeholder="" {...register("clientSID", { required: true })} />
            </Field>

            {/* CAMPAIGN NAME */}
            <Field label="Campaign Name" required>
              <Input placeholder="" {...register("campaignName", { required: true })} />
            </Field>

            {/* LIVE DATE */}
            <Field label="Live Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className={`w-full justify-start ${!watch("liveDate") ? "text-muted-foreground" : ""}`}>
                    {watch("liveDate") ? format(watch("liveDate"), "PPP") : "Pick Live Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar mode="single" selected={watch("liveDate")} onSelect={(d) => setValue("liveDate", d, { shouldValidate: true })} />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("liveDate", { required: "Live date is required" })} />
            </Field>

            {/* START DATE */}
            <Field label="Start Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className={`w-full justify-start ${!watch("startDate") ? "text-muted-foreground" : ""}`}>
                    {watch("startDate") ? format(watch("startDate"), "PPP") : "Pick Start Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar mode="single" selected={watch("startDate")} onSelect={(d) => setValue("startDate", d, { shouldValidate: true })} />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("startDate", { required: "Start date is required" })} />
            </Field>

            {/* END DATE */}
            <Field label="End Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className={`w-full justify-start ${!watch("endDate") ? "text-muted-foreground" : ""}`}>
                    {watch("endDate") ? format(watch("endDate"), "PPP") : "Pick End Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar mode="single" selected={watch("endDate")} onSelect={(d) => { endDateTouched.current = true; setValue("endDate", d, { shouldValidate: true }); }} />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("endDate", { required: "End date is required" })} />
            </Field>

            {/* CLIENT */}
<Field label="Client Name" required>
  <Input
    placeholder="Enter Client Name"
    {...register("clientName", {
      required: "Client Name is required",
    })}
  />
</Field>

            {/* DELIVERY DAY */}
            <Field label="Delivery Day" required>
              <Select onValueChange={(v) => setValue("deliveryDay", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select Delivery Day" /></SelectTrigger>
                <SelectContent>{deliveryDayOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("deliveryDay", { required: true })} />
            </Field>

            {/* PACING */}
            <Field label="Pacing">
              <Select onValueChange={(v) => { setValue("pacingType", v, { shouldValidate: true }); if (v === "NA") setValue("pacingValue", ""); }}>
                <SelectTrigger><SelectValue placeholder="Select Pacing" /></SelectTrigger>
                <SelectContent>{pacingOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("pacingType")} />
            </Field>

            {selectedPacing && selectedPacing !== "NA" && (
              <Field label="Pacing Value">
                <Input placeholder="e.g. 5000 or 70%" {...register("pacingValue")} />
              </Field>
            )}

            {/* NUMBERS — negative values blocked on keydown/wheel + HTML min=0 */}
            <Field label="Allocation" required>
              <Input type="number" min={0} placeholder="" onKeyDown={blockNegativeKey} onWheel={blockWheelChange} {...register("allocation", { required: true, min: 0 })} />
            </Field>
            <Field label="Accepted" required>
              <Input type="number" min={0} placeholder="" onKeyDown={blockNegativeKey} onWheel={blockWheelChange} {...register("accepted", { required: true, min: 0 })} />
            </Field>
            <Field label="Rejected" required>
              <Input type="number" min={0} placeholder="" onKeyDown={blockNegativeKey} onWheel={blockWheelChange} {...register("rejected", { required: true, min: 0 })} />
            </Field>
            <Field label="Pending QA" required>
              <Input placeholder="" {...register("pendingQA", { required: true, min: 0 })} />
            </Field>
            <Field label="Outstanding">
              <Input type="number" placeholder="" readOnly className="cursor-not-allowed" {...register("outstanding")} />
            </Field>

            {/* PRIORITY */}
            <Field label="Priority" required>
              <Select onValueChange={(v) => setValue("priority", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select Priority" /></SelectTrigger>
                <SelectContent>{priorityOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("priority", { required: true })} />
            </Field>

            {/* CM */}
            <Field label="Campaign Manager" required>
              <Select onValueChange={(v) => setValue("CM", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select CM" /></SelectTrigger>
                <SelectContent>{CMs.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("CM", { required: "CM is required" })} />
            </Field>

            {/* CSM */}
            <Field label="Client Success Manager Owner" required>
              {isCsmUser ? (
                <>
                  <Input disabled value={currentUser?.name} />
                  <input type="hidden" {...register("CSM", { required: true })} value={currentUser?.name} />
                </>
              ) : (
                <>
                  <Select onValueChange={(v) => setValue("CSM", v, { shouldValidate: true })}>
                    <SelectTrigger><SelectValue placeholder="Select CSM Owner" /></SelectTrigger>
                    <SelectContent>{csmList.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <input type="hidden" {...register("CSM", { required: "CSM Owner is required" })} />
                </>
              )}
            </Field>

            {/* SALES MANAGERS */}
            <Field label="Sales Manager(s)" required>
              <Select onValueChange={(v) => {
                const prev = watch("salesManagers") || [];
                if (!prev.includes(v)) setValue("salesManagers", [...prev, v], { shouldValidate: true });
              }}>
                <SelectTrigger><SelectValue placeholder="Select Sales Manager(s)" /></SelectTrigger>
                <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              <input type="hidden" {...register("salesManagers", { validate: (v) => v?.length > 0 || "At least one Sales Manager is required" })} />
            </Field>

            <div className="col-span-2">
              {Array.isArray(watch("salesManagers")) && watch("salesManagers").length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {watch("salesManagers").map((name) => (
                    <span key={name} className="flex items-center gap-2 rounded-full bg-neon px-3 py-1 text-sm text-gray-500">
                      {name}
                      <button type="button" onClick={() => setValue("salesManagers", watch("salesManagers").filter((m) => m !== name), { shouldValidate: true })} className="text-gray-500 hover:text-red-500">✕</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Selected Managers: None</p>
              )}
            </div>

            {/* IO/PO & SO — optional */}
            <Field label="IO/PO Number">
              <Input placeholder="" {...register("io_po_number")} />
            </Field>
            <Field label="SO Number">
              <Input placeholder="" {...register("so_number")} />
            </Field>

            <div className="col-span-2 flex justify-end gap-2 mt-4">
              <Button type="reset" variant="secondary" onClick={() => reset()}>Reset</Button>
              <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <Footer />
    </div>
  );
}